import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useMemo, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Text from '../../../typography/Text';
import Link from '../../../typography/Link';
import Icon from '../../../general/Icon';
import { useClasses } from '../../../../utils/overrides';
import { createUseStyles, useTheme } from '../../../../utils/styles';
import { Transition } from '../transitions';
import { TYPES } from '../constants';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Toast');
const Toast = /*#__PURE__*/memo(_ref => {
  let {
    classes: classesProp,
    className: classNameProp,
    override,
    id,
    type,
    onClick,
    closeOnClick,
    closeButton,
    useDefaultCloseButton,
    onClose,
    title,
    text,
    icon,
    isActive,
    clearDeletedToast,
    onClickLink,
    linkText,
    transition = 'slide',
    content = null
  } = _ref;
  const theme = useTheme();
  const classes = useClasses(useStyles, classesProp);
  const rootClassName = classnames(classes.root, {
    [classes[TYPES.success]]: !content && type === TYPES.success,
    [classes[TYPES.warning]]: !content && type === TYPES.warning,
    [classes[TYPES.error]]: !content && type === TYPES.error,
    [classes[TYPES.info]]: !content && type === TYPES.info
  }, classNameProp);
  const handleOnClose = useCallback(e => {
    e.stopPropagation();
    onClose();
  }, [onClose]);
  const handleOnClick = useCallback(() => {
    if (onClick) onClick();
    if (!closeOnClick) return;
    onClose();
  }, [closeOnClick, onClose, onClick]);
  const handleOnClickLink = useCallback(e => {
    e.stopPropagation();
    onClickLink();
  }, [onClickLink]);
  const toastContent = useMemo(() => {
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes.header
    }, /*#__PURE__*/React.createElement(Text, {
      className: classes.title,
      type: "subtitle",
      bold: true
    }, title), closeButton && closeButton, !closeButton && useDefaultCloseButton && /*#__PURE__*/React.createElement(Icon, {
      className: classes.close,
      name: "close",
      size: "large",
      onClick: handleOnClose
    })), !text && content, !content && /*#__PURE__*/React.createElement("div", {
      className: classes.content
    }, /*#__PURE__*/React.createElement(Text, {
      className: classes.text,
      type: "caption",
      color: theme.colors.utility.textSecondary
    }, text), onClickLink && linkText && /*#__PURE__*/React.createElement(Link, {
      className: classes.link,
      type: "caption",
      onClick: handleOnClickLink
    }, linkText)));
  }, [classes, content, text, title, handleOnClose, closeButton, useDefaultCloseButton, onClickLink, handleOnClickLink, linkText, theme]);
  const iconType = useMemo(() => {
    const icons = {
      [TYPES.success]: {
        name: 'taskChecked',
        color: theme.colors.semantic.positive500
      },
      [TYPES.warning]: {
        name: 'warning',
        color: theme.colors.semantic.focus500
      },
      [TYPES.error]: {
        name: 'error',
        color: theme.colors.semantic.negative500
      },
      [TYPES.info]: {
        name: 'info',
        color: theme.colors.semantic.info500
      }
    };
    if (!icons[type]) return null;
    return /*#__PURE__*/React.createElement(Icon, {
      name: icons[type].name,
      color: icons[type].color
    });
  }, [type, theme]);
  const toastWrapperClassName = classnames(classes.toastWrapper, {
    [classes.withIcon]: !!iconType || !!icon
  });
  return /*#__PURE__*/React.createElement(Transition, {
    transition: transition,
    transitionKey: id,
    show: isActive,
    timeout: 300,
    onExited: () => clearDeletedToast(id)
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: rootClassName
  }, override.Toast, {
    onClick: handleOnClick
  }), (iconType || icon) && /*#__PURE__*/React.createElement("div", _extends({
    className: toastWrapperClassName
  }, override.ToastWrapper), /*#__PURE__*/React.createElement("div", {
    className: classes.iconBox
  }, iconType || icon), /*#__PURE__*/React.createElement("div", {
    className: classes.contentBox
  }, toastContent)), !iconType && !content && !icon && /*#__PURE__*/React.createElement("div", _extends({
    className: toastWrapperClassName
  }, override.ToastWrapper), toastContent), !title && !icon && !iconType && content));
});
process.env.NODE_ENV !== "production" ? Toast.propTypes = {
  className: PropTypes.string,
  overrides: PropTypes.object,
  containerId: PropTypes.any,
  content: PropTypes.element,
  text: PropTypes.string,
  title: PropTypes.string,
  closeButton: PropTypes.element,
  closeButtonClassName: PropTypes.string,
  closeOnClick: PropTypes.bool,
  newestOnTop: PropTypes.bool,
  autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  useDefaultCloseButton: PropTypes.bool
} : void 0;
export default Toast;